import { use } from 'react';
import { useTranslation } from 'react-i18next';
import { HandshakeOutlined } from '@mui/icons-material';
import { Typography } from '@mui/material';
import Tooltip from '@mui/material/Tooltip';
import Constants from 'style/constants';
import LoadingIndicator from 'shared/components/loading-indicator/loading-indicator';
import { ColumnDefinition } from 'shared/components/table/columnDefinition';
import Table from 'shared/components/table/table';
import RowItem from 'shared/components/table/types/row-item';

import LocationFeatureSubscriptionsProviderContext from 'features/feature-subscriptions/contexts/location-feature-subscriptions-provider-context';
import TenantFeatureSubscriptionsProviderContext from 'features/feature-subscriptions/contexts/tenant-feature-subscriptions-provider-context';
import { LocationFeatureSubscription } from 'features/feature-subscriptions/models/location-feature-subscription';
import styles from './location-feature-subscriptions.module.scss';

const LocationFeatureSubscriptions = (): React.JSX.Element => {
  const { t } = useTranslation('featureSubscriptions');

  const { selectedItem, openDetails, readLocationFeatureSubscriptionsData, readLocationFeatureSubscriptionsIsLoading } = use(
    LocationFeatureSubscriptionsProviderContext,
  );

  const {
    readTenantFeatureSubscriptionsData,
    viewingMode: tenantViewingMode,
    hasActiveTenantSubscription,
  } = use(TenantFeatureSubscriptionsProviderContext);

  const isTenantEditing = tenantViewingMode === 'editing';

  const renderIcons = (item: LocationFeatureSubscription): React.JSX.Element => (
    <div className={styles.icons}>
      {item.featureSubscriptions.map((subscription) => (
        <Tooltip
          key={subscription.featureSubscriptionType}
          title={t(`organisationSubscriptions.subscriptions.${subscription.featureSubscriptionType}`)}
        >
          <HandshakeOutlined className={`${styles.icon} ${subscription.isActive ? styles.iconActive : styles.iconInactive}`} />
        </Tooltip>
      ))}
    </div>
  );

  const columns: ColumnDefinition<LocationFeatureSubscription, keyof LocationFeatureSubscription>[] = [
    { key: 'locationName', label: t('locationSubscriptionsTable.column.locationName') },
    {
      key: 'locationId',
      label: t('locationSubscriptionsTable.column.featureSubscriptions'),
      renderCustomContentProvider: renderIcons,
      disableSort: true,
    },
  ];

  const renderNoSubscriptionsMessage = (): React.JSX.Element => (
    <Typography variant="body2" sx={{ color: Constants.Colors.onSurfaceVariant }}>
      {t('organisationSubscriptions.noActiveSubscriptions')}
    </Typography>
  );

  const renderLocationFeatureSubscriptionsTable = (): React.JSX.Element | null => {
    if (!readLocationFeatureSubscriptionsData?.locationFeatureSubscriptions.length) return null;

    return (
      <Table
        data={readLocationFeatureSubscriptionsData.locationFeatureSubscriptions.map<RowItem<LocationFeatureSubscription>>(
          (locationFeatureSubscription: LocationFeatureSubscription) => ({ data: locationFeatureSubscription }),
        )}
        onItemClick={(item: LocationFeatureSubscription) => openDetails(item)}
        columns={columns}
        currentItem={selectedItem}
        rowIdentifier={(item: LocationFeatureSubscription) => item.locationId}
        useBuiltInSorter={true}
        disabled={isTenantEditing}
      />
    );
  };

  if (readLocationFeatureSubscriptionsIsLoading) {
    return <LoadingIndicator />;
  }

  return (
    <div className={styles.container}>
      {readTenantFeatureSubscriptionsData && !hasActiveTenantSubscription && renderNoSubscriptionsMessage()}
      {hasActiveTenantSubscription && renderLocationFeatureSubscriptionsTable()}
    </div>
  );
};

export default LocationFeatureSubscriptions;
